/**
 * <s-product-gallery> — One large stage with a thumbnail rail on the product page.
 *
 * Clicking a thumbnail shows the matching stage slide. Variant changes are handled
 * server-side: s-variant-picker.js refetches the "product-media" partial, which
 * renders the selected variant's image as the active slide.
 *
 * Required child markup:
 *   [data-gallery-slide][data-media-id] — Stage slides; inactive slides carry `hidden`.
 *   [data-gallery-thumb][data-media-id] — Thumbnail buttons; the active one has aria-current="true".
 */
class SProductGallery extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;

    this.select(thumb.dataset.mediaId);
  };

  select(mediaId) {
    if (!mediaId) return;

    for (const slide of this.querySelectorAll('[data-gallery-slide]')) {
      const isActive = slide.dataset.mediaId === mediaId;
      slide.hidden = !isActive;
      if (!isActive) {
        for (const video of slide.querySelectorAll('video')) video.pause();
      }
    }

    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === mediaId));
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
